/**
 * Compact remaining-time for the Cancels column.
 *
 * Pulse v1 showed `⏱ 2d` / `⏱ past` from `expiryAt` (Amazon delivery-window
 * end). v2 keeps the deadline as an absolute timestamp for sort + tooltip,
 * and renders a short remaining-time cell: `39d` / `24d` / `2h` / `45m`.
 * Missing deadlines stay `—` — never a fake 99d.
 */

const MINUTE_MS = 60_000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS
/** Visual cue threshold — amber/red when the window closes within 3 days. */
export const CANCEL_SOON_MS = 3 * DAY_MS

export type CancelCountdownTone = 'ok' | 'soon' | 'overdue' | 'missing'

export interface CancelCountdown {
  label: string
  tone: CancelCountdownTone
  ariaLabel: string
  at: number | null
}

export function cancelCountdown(
  iso: string | null | undefined,
  now: number = Date.now(),
): CancelCountdown {
  if (!iso) {
    return { label: '—', tone: 'missing', ariaLabel: 'no cancel deadline', at: null }
  }
  const at = Date.parse(iso)
  if (Number.isNaN(at)) {
    return { label: '—', tone: 'missing', ariaLabel: 'no cancel deadline', at: null }
  }

  const delta = at - now
  const abs = Math.abs(delta)
  const overdue = delta < 0

  if (overdue) {
    if (abs >= DAY_MS) {
      const days = Math.floor(abs / DAY_MS)
      return {
        label: `-${days}d`,
        tone: 'overdue',
        ariaLabel: `cancel window overdue by ${days} day${days === 1 ? '' : 's'}`,
        at,
      }
    }
    return { label: 'overdue', tone: 'overdue', ariaLabel: 'cancel window overdue', at }
  }

  let label: string
  let unitPhrase: string
  if (abs >= DAY_MS) {
    const days = Math.floor(abs / DAY_MS)
    label = `${days}d`
    unitPhrase = `${days} day${days === 1 ? '' : 's'}`
  } else if (abs >= HOUR_MS) {
    const hours = Math.max(1, Math.round(abs / HOUR_MS))
    label = `${hours}h`
    unitPhrase = `${hours} hour${hours === 1 ? '' : 's'}`
  } else {
    const minutes = Math.max(1, Math.round(abs / MINUTE_MS))
    label = `${minutes}m`
    unitPhrase = `${minutes} minute${minutes === 1 ? '' : 's'}`
  }

  return {
    label,
    tone: abs <= CANCEL_SOON_MS ? 'soon' : 'ok',
    ariaLabel: `cancels in ${unitPhrase}`,
    at,
  }
}
